import { addMonths, endOfMonth, format, parse, startOfMonth } from 'date-fns';

const DATE_FNS_FORMAT = 'yyyyMMdd';
const MAX_MONTHS = 12;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * @description Generates month options needed for the TimeFrameDropdown
 */
export const getMonthsForTimeFrameDropdown = () => {
  const monthDropdownOptions = [];
  const currentDate = new Date();
  currentDate.setDate(1);

  for (let i = 0; i < MAX_MONTHS; i++) {
    monthDropdownOptions.push({
      label: `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`,
      timeFrameValue: format(currentDate, DATE_FNS_FORMAT),
    });

    currentDate.setMonth(currentDate.getMonth() - 1);
  }

  return monthDropdownOptions;
};

/**
 * @description Helper function to be used with TimeFrameDropdown when passing dates as params to endpoints.
 * Returns UTC start and end dates formatted as yyyyMMdd
 */
export const getDateRangesFromDaysBackOrStartDate = (daysBack: number, startDate?: string) => {
  if (startDate) {
    return getDateRangesFromStartDate(startDate);
  }

  return getDateRangesFromDaysBack(daysBack);
};

/**
 * @description Helper function to be used with TimeFrameDropdown when passing dates as params to endpoints.
 * @param startDate yyyyMMdd
 * Returns UTC start and end dates formatted as yyyyMMdd
 */
const getDateRangesFromStartDate = (
  startDate: string,
): { startDateUTC: string; endDateUTC: string; endDateUTCExclusive: string } => {
  const dateUTC = getUTCDateMS(new Date());
  const startDateUTC = parse(startDate, DATE_FNS_FORMAT, dateUTC);

  const endOfMonthDate = endOfMonth(startDateUTC);
  const endDateUTC = format(endOfMonthDate, DATE_FNS_FORMAT);

  // In order to include leads data up to the current day,
  // we need to request an end date of the first day of the following month
  const nextMonth = addMonths(startDateUTC, 1);
  const startOfNextMonth = startOfMonth(nextMonth);
  const endDateUTCExclusive = format(startOfNextMonth, DATE_FNS_FORMAT);

  return {
    startDateUTC: format(startDateUTC, DATE_FNS_FORMAT),
    endDateUTC,
    endDateUTCExclusive,
  };
};

/**
 * @description Helper function to be used with TimeFrameDropdown when passing dates as params to endpoints.
 * Returns UTC start and end dates formatted as yyyyMMdd
 */
const getDateRangesFromDaysBack = (
  daysBack: number,
): { startDateUTC: string; endDateUTC: string; endDateUTCExclusive: string } => {
  const startDateUTC = getStartDateUTCFromDaysBack(daysBack);

  // If daysBack is 1, we are looking for yesterday's date. End date will be equal to startDateUTC (yesterday's date)
  const endDateUTC = daysBack === 1 ? startDateUTC : getTodaysDateUTC();

  // When end date is exclusive, then endDate will be tomorrow's date, except when looking for yesterday's date
  // If daysBack is 1, we are looking for yesterday's date. End date will be equal to today's date
  const endDateUTCExclusive = daysBack === 1 ? getTodaysDateUTC() : getTomorrowsDateUTC();

  return {
    startDateUTC,
    endDateUTC,
    endDateUTCExclusive,
  };
};

/**
 * @description Returns date in UTC milliseconds
 */
export const getUTCDateMS = (date: Date) => {
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
};

/**
 * @description Returns a past date in UTC formatted as yyyyMMdd based on N days back
 */
const getStartDateUTCFromDaysBack = (daysBack: number) => {
  const date = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000);
  const dateUTC = getUTCDateMS(date);
  return format(dateUTC, DATE_FNS_FORMAT);
};

/**
 * @description Returns today's date in UTC formatted as yyyyMMdd
 */
const getTodaysDateUTC = () => {
  const todayDateUTC = getUTCDateMS(new Date());
  return format(todayDateUTC, DATE_FNS_FORMAT);
};

/**
 * @description Returns tomorrow's date in UTC formatted as yyyyMMdd
 */
const getTomorrowsDateUTC = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowDateUTC = getUTCDateMS(tomorrow);
  return format(tomorrowDateUTC, DATE_FNS_FORMAT);
};
